/**
 * Severity mapping for axe-core impact levels.
 * Maps axe-core impact values to display labels and sort order.
 */
const SEVERITY_MAP = {
    critical: { label: 'Critical', order: 0, color: 'red' },
    serious: { label: 'Serious', order: 1, color: 'orange' },
    moderate: { label: 'Moderate', order: 2, color: 'yellow' },
    minor: { label: 'Minor', order: 3, color: 'blue' },
};

export const GRADE_THRESHOLDS = [
    { max: 0, grade: 'A+', pass: true },
    { max: 5, grade: 'A', pass: true },
    { max: 15, grade: 'B', pass: true },
    { max: 30, grade: 'C', pass: false },
    { max: 60, grade: 'D', pass: false },
    { max: Infinity, grade: 'F', pass: false },
];

/**
 * Calculates a compliance grade based on the risk score.
 */
export function getGradeFromRisk(riskScore) {
    for (const t of GRADE_THRESHOLDS) {
        if (riskScore <= t.max) return { grade: t.grade, pass: t.pass };
    }
    return { grade: 'F', pass: false };
}

/**
 * Generates a plain-English "How to Fix" description from an axe-core violation.
 * @param {Object|string} violation - Violation object or rule ID string
 * @param {string|null} detectedCMS - Auto-detected CMS platform (e.g. WordPress, Shopify)
 */
export function getHowToFix(violation, detectedCMS = null) {
    // Build a comprehensive remediation guide
    const fixSuggestions = [];

    // Add the main help text
    if (violation.help) {
        fixSuggestions.push(violation.help + '.');
    }

    // Add specific guidance based on common violation types
    const id = violation.id || '';
    if (id.includes('color-contrast')) {
        fixSuggestions.push('\nEnsure text has at least 4.5:1 contrast ratio for normal text and 3:1 for large text (18pt or 14pt bold). Use a contrast checker tool to verify color combinations.\n\nEXAMPLE SOLUTION:\n/* Fails contrast */\n.text-dim { color: #888; background: #fff; }\n/* Passes contrast (4.54:1) */\n.text-accessible { color: #757575; background: #fff; }');
    } else if (id.includes('image-alt') || id === 'image-alt') {
        fixSuggestions.push('\nAdd descriptive alt text that conveys the meaning and purpose of the image. For decorative images, use an empty alt="" attribute.\n\nEXAMPLE SOLUTION:\n<!-- Meaningful content -->\n<img src="chart.png" alt="Sales increased by 20% in Q3" />\n\n<!-- Decorative only -->\n<img src="spacer.gif" alt="" aria-hidden="true" />');
    } else if (id.includes('link-name')) {
        fixSuggestions.push('\nEnsure all links have discernible text. Add visible text content, aria-label, or aria-labelledby. Avoid generic text like "click here".\n\nEXAMPLE SOLUTION:\n<!-- Accessible Link -->\n<a href="/profile" aria-label="View User Profile">\n  <svg class="icon-user" />\n</a>');
    } else if (id.includes('button-name')) {
        fixSuggestions.push('\nEnsure buttons have accessible names via visible text, aria-label, aria-labelledby, or title attribute.\n\nEXAMPLE SOLUTION:\n<!-- Accessible Button -->\n<button aria-label="Close dialog" onClick={closeMenu}>\n  X\n</button>');
    } else if (id.includes('label') || id === 'label') {
        fixSuggestions.push('\nAssociate form inputs with labels using the for/id attributes, or wrap inputs within label elements. Alternatively, use aria-label or aria-labelledby.\n\nEXAMPLE SOLUTION:\n<!-- Explicit Label -->\n<label for="username">Username:</label>\n<input type="text" id="username" name="username" />');
    } else if (id.includes('aria-valid-attr')) {
        fixSuggestions.push('\nRemove or correct invalid ARIA attributes. Check that attribute names are spelled correctly and values match the expected format.\n\nEXAMPLE SOLUTION:\n<!-- Invalid -->\n<div aria-disable="true">...</div>\n\n<!-- Valid -->\n<div aria-disabled="true">...</div>');
    } else if (id.includes('aria-hidden')) {
        fixSuggestions.push('\nEnsure aria-hidden elements do not contain focusable content. Remove focusable elements or the aria-hidden attribute.\n\nEXAMPLE SOLUTION:\n<!-- Invalid (Focusable element hidden from screen readers) -->\n<div aria-hidden="true">\n  <button>Click Me</button>\n</div>');
    } else if (id.includes('heading-order')) {
        fixSuggestions.push('\nEnsure heading levels increase by one (h1 → h2 → h3). Do not skip levels. Structure headings to create a logical document outline.\n\nEXAMPLE SOLUTION:\n<!-- Logical Hierarchy -->\n<h1>Main Title</h1>\n<h2>Subtitle</h2>\n<h3>Section Header</h3>');
    } else if (id.includes('landmark') || id.includes('region')) {
        fixSuggestions.push('\nEnsure page content is contained within appropriate landmark regions (main, nav, aside, etc.) to help screen reader users navigate.\n\nEXAMPLE SOLUTION:\n<body>\n  <nav aria-label="Main Navigation">...</nav>\n  <main>\n    <!-- Primary content goes here -->\n  </main>\n  <footer>...</footer>\n</body>');
    } else if (id.includes('frame-title')) {
        fixSuggestions.push('\nAdd a descriptive title attribute to all iframe and frame elements to identify their content or purpose.\n\nEXAMPLE SOLUTION:\n<iframe src="https://youtube.com/..." title="Product Demonstration Video"></iframe>');
    } else {
        fixSuggestions.push('\nReview the affected elements and apply the appropriate WCAG 2.1 AA fix. Test with assistive technologies after making changes.');
    }

    // Add CMS-specific remediation advice
    if (detectedCMS) {
        fixSuggestions.push(`\n\nCMS GUIDANCE (${detectedCMS.toUpperCase()}):`);
        if (detectedCMS === 'WordPress') {
            fixSuggestions.push('\nCheck your theme\'s header.php, footer.php, or template parts. If the issue is in page content, edit the Gutenberg block. Consider installing an accessibility plugin like WP Accessibility for global fixes.');
        } else if (detectedCMS === 'Shopify') {
            fixSuggestions.push('\nEdit your theme\'s Liquid templates (e.g., theme.liquid, product.liquid). Look for snippet files that generate the affected HTML.');
        } else if (detectedCMS === 'Wix') {
            fixSuggestions.push('\nUse the Wix Accessibility Wizard in the editor. Select the element and use the Accessibility panel to add alt text, ARIA attributes, or heading tags.');
        } else if (detectedCMS === 'Squarespace') {
            fixSuggestions.push('\nEdit the block containing the element. Squarespace limits direct HTML editing, but you can usually add alt text to image blocks or use the format menu to change heading levels.');
        } else if (detectedCMS === 'Webflow') {
            fixSuggestions.push('\nSelect the element in the Webflow Designer. Open the Settings panel (D) to add custom attributes, or use the Image settings to add Alt text.');
        } else {
            fixSuggestions.push(`\nReview your ${detectedCMS} template files or administration dashboard to modify the affected elements.`);
        }
    }

    return fixSuggestions.join('');
}

export const VIOLATION_COSTS = {
    critical: 15000,
    serious: 8000,
    moderate: 2000,
    minor: 500,
};

/**
 * Transforms raw axe-core JSON results into grouped, plain-English data.
 *
 * @param {Object} results - Raw axe-core results { violations, passes, incomplete, detectedCMS }
 * @param {string|null} explicitCMS - Optional override for CMS detection
 * @returns {Object} Parsed results grouped by severity
 */
export function parseAxeResults(results, explicitCMS = null) {
    if (!results) return { groups: [], summary: {} };

    const detectedCMS = explicitCMS || results.detectedCMS || null;

    const violations = Array.isArray(results.violations) ? results.violations : [];
    const passesCount = results.passesCount ?? results.passes?.length ?? 0;
    const incompleteCount = results.incompleteCount ?? results.incomplete?.length ?? 0;

    // Extract privacy data (extensions send privacyResults, agents send privacyFindings array)
    let privacyFindings = [];
    if (results.privacyFindings && Array.isArray(results.privacyFindings)) {
        privacyFindings = results.privacyFindings;
    } else if (results.privacyResults) {
        // Single page extension scan
        privacyFindings = [{ url: results.url || 'Unknown', ...results.privacyResults }];
    }

    // Group violations by severity/impact
    const grouped = {};
    const ruleStats = {};
    let totalRiskCost = 0;
    let weightedImpactSum = 0;
    const nodeCountsBySeverity = { critical: 0, serious: 0, moderate: 0, minor: 0 };

    violations.forEach((violation) => {
        const impact = violation.impact || 'minor';
        const severity = SEVERITY_MAP[impact] || SEVERITY_MAP.minor;

        // Rule Stats
        if (!ruleStats[violation.id]) {
            ruleStats[violation.id] = {
                title: violation.id.replace(/-/g, ' ').toUpperCase(),
                count: 0,
                impact,
                description: violation.description || violation.help
            };
        }
        ruleStats[violation.id].count += violation.nodes?.length || 0;

        if (!grouped[impact]) {
            grouped[impact] = {
                ...severity,
                impact,
                violations: [],
            };
        }

        // Calculate risk metrics
        const nodeCount = violation.nodes?.length || 0;
        totalRiskCost += (VIOLATION_COSTS[impact] || 0) * nodeCount;
        nodeCountsBySeverity[impact] = (nodeCountsBySeverity[impact] || 0) + nodeCount;

        // Weight: critical=10, serious=5, moderate=2, minor=1
        const weight = impact === 'critical' ? 10 : impact === 'serious' ? 5 : impact === 'moderate' ? 2 : 1;
        weightedImpactSum += weight * nodeCount;

        // Extract affected HTML nodes
        const affectedNodes = violation.nodes.map((node) => ({
            html: node.html,
            target: Array.isArray(node.target) ? node.target.join(', ') : (node.target || ''),
            failureSummary: node.failureSummary || '',
            pageUrl: node.pageUrl || '',
        }));

        // Determine if it's strictly a best practice
        const isBestPractice = violation.tags?.includes('best-practice') && !violation.tags.some(t => t.startsWith('wcag') || t === 'section508');

        grouped[impact].violations.push({
            id: violation.id,
            description: violation.description,
            help: violation.help,
            helpUrl: violation.helpUrl,
            howToFix: getHowToFix(violation, detectedCMS),
            tags: violation.tags || [],
            isBestPractice,
            nodes: affectedNodes,
            nodeCount: affectedNodes.length,
        });
    });

    // Sort groups by severity order
    const groups = Object.values(grouped).sort((a, b) => a.order - b.order);

    // Summary stats
    const summary = {
        detectedCMS,
        totalViolations: violations.length,
        totalPasses: passesCount,
        totalIncomplete: incompleteCount,
        totalRiskCost,
        riskScore: weightedImpactSum === 0 ? 0 : Math.min(100, Math.round(100 * (1 - Math.exp(-weightedImpactSum / 30)))),
        totalAffectedNodes: violations.reduce(
            (sum, v) => sum + (v.totalNodes || v.nodes?.length || 0),
            0
        ),
        bySeverity: {
            critical: grouped.critical?.violations.length || 0,
            serious: grouped.serious?.violations.length || 0,
            moderate: grouped.moderate?.violations.length || 0,
            minor: grouped.minor?.violations.length || 0,
        },
        nodeCountsBySeverity,
        ruleStats,
        privacy: privacyFindings
    };

    return { groups, summary };
}

/**
 * Deduplicates violations across multiple scan results for the same domain.
 * Useful for "Sites" view or "Group by Domain" in Dashboard.
 * 
 * @param {Array} resultsArray - Array of scan results objects
 * @returns {Object} Deduplicated summary stats
 */
export function deduplicateViolations(resultsArray, trackingData = {}) {
    if (!resultsArray || resultsArray.length === 0) return null;

    const uniqueViolations = new Map(); // key: violationId + targetSelector
    let totalPasses = 0;
    let totalIncomplete = 0;
    let detectedCMS = null;
    const uniquePages = new Set();

    resultsArray.forEach(results => {
        if (!results) return;
        if (!detectedCMS && results.detectedCMS) detectedCMS = results.detectedCMS;

        if (results.url) uniquePages.add(results.url);

        totalPasses += results.passesCount ?? results.passes?.length ?? 0;
        totalIncomplete += results.incompleteCount ?? results.incomplete?.length ?? 0;

        const violations = Array.isArray(results.violations) ? results.violations : [];
        violations.forEach(v => {
            const impact = v.impact || 'minor';
            const cost = VIOLATION_COSTS[impact] || 0;
            const weight = impact === 'critical' ? 10 : impact === 'serious' ? 5 : impact === 'moderate' ? 2 : 1;

            if (v.pageUrls) v.pageUrls.forEach(u => uniquePages.add(u));

            (v.nodes || []).forEach(node => {
                if (node.pageUrl) uniquePages.add(node.pageUrl);
                const targetKey = `${v.id}|${node.target}|${node.pageUrl || ''}`;
                if (!uniqueViolations.has(targetKey)) {
                    uniqueViolations.set(targetKey, {
                        impact,
                        cost,
                        weight,
                        violationId: v.id,
                        description: v.description,
                        help: v.help,
                        tags: v.tags || [],
                        node
                    });
                }
            });
        });
    });

    let totalRiskCost = 0;
    let weightedImpactSum = 0;
    const bySeverity = { critical: 0, serious: 0, moderate: 0, minor: 0 };
    const nodeCountsBySeverity = { critical: 0, serious: 0, moderate: 0, minor: 0 };
    const ruleStats = {};

    // Grouping for the summary count (by rule ID)
    const ruleIdsBySeverity = { critical: new Set(), serious: new Set(), moderate: new Set(), minor: new Set() };

    uniqueViolations.forEach((v, key) => {
        const isFixed = trackingData[key]?.status === 'fixed';
        if (!isFixed) {
            totalRiskCost += v.cost;
            weightedImpactSum += v.weight;
            nodeCountsBySeverity[v.impact]++;
            ruleIdsBySeverity[v.impact].add(v.violationId);

            if (!ruleStats[v.violationId]) {
                ruleStats[v.violationId] = {
                    title: v.violationId.replace(/-/g, ' ').toUpperCase(),
                    count: 0,
                    impact: v.impact,
                    description: v.description || v.help
                };
            }
            ruleStats[v.violationId].count++;
        }
    });

    const uniquePageCount = uniquePages.size || 1;
    const avgWeightedImpact = weightedImpactSum / uniquePageCount;

    const summary = {
        detectedCMS,
        totalViolations: Object.values(ruleIdsBySeverity).reduce((sum, set) => sum + set.size, 0),
        totalPasses,
        totalIncomplete,
        totalRiskCost,
        riskScore: avgWeightedImpact === 0 ? 0 : Math.min(100, Math.round(100 * (1 - Math.exp(-avgWeightedImpact / 30)))),
        totalAffectedNodes: uniqueViolations.size,
        uniquePageCount,
        bySeverity: {
            critical: ruleIdsBySeverity.critical.size,
            serious: ruleIdsBySeverity.serious.size,
            moderate: ruleIdsBySeverity.moderate.size,
            minor: ruleIdsBySeverity.minor.size,
        },
        nodeCountsBySeverity,
        ruleStats,
        violations: Array.from(uniqueViolations.entries()).map(([key, v]) => ({
            key,
            ...v
        }))
    };

    return summary;
}

/**
 * Maps a compliance type to a human-readable label.
 */
export function getComplianceLabel(type) {
    const labels = {
        'state-local': 'State / Local Government',
        federal: 'Federal / Federally Funded',
        private: 'Private Business',
        'wcag2a': 'WCAG 2.0 A',
        'wcag2aa': 'WCAG 2.0 AA',
        'wcag21a': 'WCAG 2.1 A',
        'wcag21aa': 'WCAG 2.1 AA',
        'section508': 'Section 508',
    };
    return labels[type] || type;
}
